import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OnboardingNav from '../components/onboarding/OnboardingNav';
import PrimaryButton from '../components/onboarding/PrimaryButton';
import StepProgress from '../components/onboarding/StepProgress';
import { useGlobalState } from '../components/AppContext';
import { normalizeEmail } from '../utils/onboardingValidation';
import { getPinnedOrDirectCallable } from '../utils/pinnedCallables';
import { getTrialStartedCtaCopy } from '../utils/signupMessaging';
import { trackOpenAiOnboardingStep } from '../utils/trackOpenAiOnboardingStep';
import { trackTrialStarted } from '../utils/trackTrialStarted';
import {
  persistTrialStartedState,
  readTrialStartedHandoffUrl,
  readTrialStartedState,
  TRIAL_STARTED_HANDOFF_DISABLED_TOOLTIP,
  TRIAL_STARTED_HANDOFF_LOADING_TOOLTIP,
} from '../utils/trialStartedSession';
import '../assets/css/Onboarding.css';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * @param {number} ms
 * @returns {string}
 */
function formatTrialDate(ms) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleDateString();
  }
}

/**
 * BWC-4795 — Trial Started (after workspace provision).
 */
function OnboardingTrialStarted() {
  const navigate = useNavigate();
  const location = useLocation();
  const [globalEmail] = useGlobalState('email');
  const email = normalizeEmail(location.state?.email || globalEmail || '');
  const storedTrialState = useMemo(
    () => (email ? readTrialStartedState(email) : null),
    [email]
  );

  const planName =
    location.state?.planName || storedTrialState?.planName || 'Free Trial';
  const publishingAllowance =
    location.state?.publishingAllowance ||
    storedTrialState?.publishingAllowance ||
    '1 channel';
  const trialStartedAt =
    Number(location.state?.trialStartedAt || storedTrialState?.trialStartedAt) ||
    Date.now();
  const trialEndsAt =
    Number(location.state?.trialEndsAt || storedTrialState?.trialEndsAt) ||
    trialStartedAt + 14 * DAY_MS;

  const initialHandoffUrl =
    location.state?.handoffUrl || readTrialStartedHandoffUrl(email) || '';
  const [handoffUrl, setHandoffUrl] = useState(initialHandoffUrl);
  const [handoffLoading, setHandoffLoading] = useState(!initialHandoffUrl);
  const [handoffError, setHandoffError] = useState(false);
  const [showHandoffTooltip, setShowHandoffTooltip] = useState(false);
  const trialConversionTracked = useRef(false);
  const ctaCopy = useMemo(() => getTrialStartedCtaCopy(), []);
  const expiryLabel = useMemo(
    () => formatTrialDate(trialEndsAt),
    [trialEndsAt]
  );

  const createOnboardingWorkspace = useMemo(
    () =>
      getPinnedOrDirectCallable('createOnboardingWorkspace', {
        timeout: 320000,
      }),
    []
  );

  useEffect(() => {
    if (!email) {
      navigate('/signup', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (!email || trialConversionTracked.current) {
      return;
    }
    trialConversionTracked.current = true;
    trackTrialStarted({
      planId: String(planName || 'free_trial')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_'),
    });
    trackOpenAiOnboardingStep(4, 'completed');
  }, [email, planName]);

  useEffect(() => {
    const previous = document.body.style.cssText;
    document.body.style.cssText =
      'margin:0;padding:0;min-height:100vh;display:block;overflow-x:hidden;background:#f5f7fa;background-image:none;';
    return () => {
      document.body.style.cssText = previous;
    };
  }, []);

  useEffect(() => {
    if (!email || initialHandoffUrl) {
      return undefined;
    }

    let cancelled = false;
    setHandoffLoading(true);
    setHandoffError(false);

    void createOnboardingWorkspace({ email })
      .then((result) => {
        if (cancelled || result?.data?.code !== 200) {
          throw new Error(result?.data?.message || 'Could not load tutorial link');
        }
        const nextHandoffUrl = String(result.data.handoffUrl || '').trim();
        if (!nextHandoffUrl) {
          throw new Error('Missing handoff URL');
        }
        persistTrialStartedState(email, {
          email,
          planName: result.data.planName,
          trialStartedAt: result.data.trialStartedAt,
          trialEndsAt: result.data.trialEndsAt,
          publishingAllowance: result.data.publishingAllowance,
          siteName: result.data.siteName,
          orgId: result.data.orgId,
          handoffUrl: nextHandoffUrl,
        });
        setHandoffUrl(nextHandoffUrl);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to refresh onboarding handoff URL:', error);
          setHandoffError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHandoffLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [createOnboardingWorkspace, email, initialHandoffUrl]);

  if (!email) {
    return null;
  }

  const handleContinue = () => {
    if (handoffUrl) {
      window.location.assign(handoffUrl);
      return;
    }
    console.error('Missing onboarding handoff URL after workspace provision');
  };

  const buttonDisabled = !handoffUrl;
  const disabledTooltip = handoffLoading
    ? TRIAL_STARTED_HANDOFF_LOADING_TOOLTIP
    : handoffError || !handoffUrl
      ? TRIAL_STARTED_HANDOFF_DISABLED_TOOLTIP
      : undefined;

  return (
    <div className="onboarding-verify-page">
      <OnboardingNav />
      <main className="onboarding-verify-stage">
        <div className="onboarding-trial-card">
          <div className="onboarding-workspace-tracker">
            <StepProgress activeStep={4} />
            <p className="onboarding-step-label">Step 4 of 4</p>
          </div>
          <div className="onboarding-trial-icon" aria-hidden>
            ✓
          </div>
          <h1 className="onboarding-trial-title">{ctaCopy.heading}</h1>
          <p className="onboarding-trial-copy">{ctaCopy.lead}</p>

          <div className="onboarding-trial-notice" role="status">
            <p className="onboarding-trial-notice-copy">{ctaCopy.body}</p>
          </div>

          <dl className="onboarding-trial-info">
            <div>
              <dt>Plan</dt>
              <dd>{planName}</dd>
            </div>
            <div>
              <dt>Trial ends</dt>
              <dd>{expiryLabel}</dd>
            </div>
            <div>
              <dt>Publishing</dt>
              <dd>{publishingAllowance}</dd>
            </div>
          </dl>

          <PrimaryButton
            type="button"
            showArrow
            disabled={buttonDisabled}
            loading={handoffLoading}
            disabledTooltip={disabledTooltip}
            showDisabledTooltip={
              (showHandoffTooltip && buttonDisabled && !handoffLoading) ||
              handoffLoading
            }
            onShowDisabledTooltip={() => setShowHandoffTooltip(true)}
            onClick={handleContinue}
          >
            {ctaCopy.buttonLabel}
          </PrimaryButton>
        </div>
      </main>
    </div>
  );
}

export default OnboardingTrialStarted;
